/**
 * nodeSync — que el canvas muestre SIEMPRE lo que hay en el store.
 *
 * Problema: los componentes de nodo copian `data` a su estado local al montarse
 * (useState(data.prompt)) y lo vuelven a escribir con updateNodeData en un
 * useEffect. Eso causa tres fallas:
 *   1. Al abrir un proyecto, esa escritura de montaje marca "sin guardar"
 *      (punto amarillo) aunque nadie tocó nada.
 *   2. Cuando el dato cambia desde afuera (Recargar, LMX, otra pestaña), el
 *      nodo sigue mostrando su copia vieja y, peor, la puede volver a escribir.
 *   3. Por eso "Recargar" en el modal de conflicto no descartaba lo local.
 *
 * Solución sin tocar los ~30 componentes de nodo:
 *   - stampExternal: cuando los nodos llegan desde afuera, a los que cambiaron
 *     se les sube data.__extRev. El envoltorio de nodos (withNodeTooltip) usa
 *     ese número como `key` → el nodo se vuelve a montar y lee el dato nuevo.
 *   - markHydrating/isHydrating: durante ~1,5 s después de cargar o sincronizar,
 *     lo que un nodo escriba al montarse NO es un cambio del usuario: no prende
 *     el punto amarillo y se suma también a la base del merge.
 *   - sameValue: si updateNodeData no cambia nada, no hace nada.
 */
export const sameValue = (a, b) =>
  a === b || JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

export const stampExternal = (next = [], prev = [], clean = (d) => d) => {
  const P = new Map(prev.map((n) => [n.id, n]));
  const changed = [];
  const nodes = next.map((n) => {
    const p = P.get(n.id);
    const rev = p?.data?.__extRev || 0;
    const differs = !p || p.type !== n.type
      || JSON.stringify(clean(n.data) ?? null) !== JSON.stringify(clean(p.data) ?? null);
    if (differs) changed.push(n.id);
    return { ...n, data: { ...(n.data || {}), __extRev: differs && p ? rev + 1 : rev } };
  });
  return { nodes, changed };
};

let quiet = { ids: new Set(), until: 0 };
export const markHydrating = (ids, ms = 1500) => {
  quiet = { ids: new Set(ids), until: Date.now() + ms };
};
export const isHydrating = (id) => Date.now() < quiet.until && quiet.ids.has(id);
